import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { generateClientReport } from '../../../services/ApiService';
import { useSystemTables } from '../../../hooks/UseSystemTables';
import styles from './ClientReportGeneration.module.css';

const ClientReportGeneration: React.FC = () => {
  const { t } = useTranslation();
  const { data: systemTablesData, isLoading: isLoadingSystemTables } = useSystemTables();
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    customerName: '',
  });

  const generateReportMutation = useMutation({
    mutationFn: generateClientReport,
    onSuccess: async (data) => {
      // Download each file
      for (const file of data.files) {
        try {
          const response = await fetch(file.url);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.fileName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } catch (error) {
          console.error(`Error downloading file ${file.fileName}:`, error);
        }
      }
    },
  });

  const handleTextFieldChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    setFormData(prev => ({
      ...prev,
      customerName: event.target.value
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    generateReportMutation.mutate(formData);
  };

  if (isLoadingSystemTables) {
    return <CircularProgress />;
  }

  const clients = systemTablesData?.data.clients || [];

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('ClientReportGeneration.Title')}</h2>
      <Paper className={styles.formContainer}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              type="date"
              label={t('General.Labels.StartDate')}
              value={formData.startDate}
              onChange={handleTextFieldChange('startDate')}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              type="date"
              label={t('General.Labels.EndDate')}
              value={formData.endDate}
              onChange={handleTextFieldChange('endDate')}
              InputLabelProps={{ shrink: true }}
              required
            />
            <FormControl fullWidth required={formData.customerName !== ''}>
              <InputLabel>{t('ClientReportGeneration.Labels.CustomerName')}</InputLabel>
              <Select
                value={formData.customerName}
                label={t('ClientReportGeneration.Labels.CustomerName')}
                onChange={handleSelectChange}
              >
                <MenuItem value="">
                  {t('General.Labels.All')}
                </MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client.clientInnerId} value={client.clientInnerId}>
                    {client.clientName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={generateReportMutation.isPending}
              sx={{ mt: 2 }}
            >
              {generateReportMutation.isPending ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('General.Labels.Confirm')
              )}
            </Button>
          </Box>
        </form>
      </Paper>
    </div>
  );
};

export default ClientReportGeneration; 